<template>
    <div ref="componentRef" class="min-h-screen bg-white">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div class="text-center mb-2.5">
                <h1 class="text-4xl xl:text-6xl font-header font-bold text-primary mb-1.5">
                    Our Team
                </h1>
                <div v-if="pageDescription"
                    class="text-xl text-gray-700 mx-auto font-body leading-relaxed content-page-description"
                    v-html="pageDescription">
                </div>
            </div>

            <div v-if="loadingMembers" class="text-center py-12">
                <p class="text-gray-600 font-body">Loading team members...</p>
            </div>
            <template v-else>
                <div v-if="founders.length > 0" class="mb-2.5">
                    <h2 class="text-3xl md:text-4xl font-header font-bold text-primary mb-1">
                        Founders
                    </h2>
                    <p v-if="foundersBlurb" class="text-lg text-gray-700 font-body leading-relaxed mb-1 mx-auto">
                        {{ foundersBlurb }}
                    </p>
                    <div class="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-12">
                        <div v-for="member in founders" :key="member.name"
                            @click="member.bio ? openDialog(member) : null" :class="[
                                'bg-white rounded-lg shadow-sm p-5 flex flex-col transition-all',
                                member.bio ? 'cursor-pointer hover:shadow-md hover:scale-105' : ''
                            ]">
                            <div v-if="member.image"
                                class="aspect-square w-full max-w-[14.4rem] mx-auto mb-5 overflow-hidden">
                                <img :src="member.image" :alt="member.name"
                                    class="w-full h-auto min-h-full object-cover" />
                            </div>
                            <div v-else
                                class="aspect-square w-full max-w-[14.4rem] mx-auto mb-5 bg-gray-200 flex items-center justify-center">
                                <span class="text-gray-400 font-body text-sm">No image</span>
                            </div>
                            <h3 class="text-xl font-header font-bold text-primary mb-2 text-center">
                                {{ member.name }}
                            </h3>
                            <p v-if="member.bio" class="text-xs text-gray-500 font-body text-center">
                                Click to read more
                            </p>
                        </div>
                    </div>
                </div>

                <div v-if="team.length > 0">
                    <h2 class="text-3xl xl:text-4xl font-header font-bold text-primary mb-1">
                        Our Team
                    </h2>
                    <div class="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-6">
                        <div v-for="member in team" :key="member.name" @click="member.bio ? openDialog(member) : null"
                            :class="[
                                'bg-white rounded-lg shadow-sm p-5 flex flex-col transition-all',
                                member.bio ? 'cursor-pointer hover:shadow-md hover:scale-105' : ''
                            ]">
                            <div v-if="member.image"
                                class="aspect-square w-full max-w-[14.4rem] mx-auto mb-5 overflow-hidden">
                                <img :src="member.image" :alt="member.name"
                                    class="w-full h-auto min-h-full object-cover" />
                            </div>
                            <div v-else
                                class="aspect-square w-full max-w-[14.4rem] mx-auto mb-5 bg-gray-200 flex items-center justify-center">
                                <span class="text-gray-400 font-body text-sm">No image</span>
                            </div>
                            <h3 class="text-xl font-header font-bold text-primary mb-2 text-center">
                                {{ member.name }}
                            </h3>
                            <p v-if="member.bio" class="text-xs text-gray-500 font-body text-center">
                                Click to read more
                            </p>
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <div v-if="selectedMember" ref="modalRef" @click="closeDialog"
            class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div @click.stop
                class="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 relative">
                <button @click="closeDialog"
                    class="absolute top-4 right-4 text-gray-400 hover:text-primary transition-colors"
                    aria-label="Close">
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
                <div class="flex flex-col items-center gap-6 mb-6">
                    <div class="w-full max-w-[14.4rem]">
                        <div class="aspect-square w-full overflow-hidden">
                            <img :src="selectedMember.image" :alt="selectedMember.name"
                                class="w-full h-auto min-h-full object-cover" />
                        </div>
                    </div>
                    <div class="w-full text-center">
                        <h2 class="text-3xl font-header font-bold text-primary mb-4">
                            {{ selectedMember.name }}
                        </h2>
                        <div class="text-gray-700 font-body leading-relaxed content-page-section"
                            v-html="selectedMember.bio">
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted } from 'vue'
import { translationService } from '../services/translation'
import { publicService } from '../services/public'
import { useDataTranslation } from '../composables/useDataTranslation'

const componentRef = ref(null)
const selectedMember = ref(null)
const modalRef = ref(null)
const teamMembers = ref([])
const loadingMembers = ref(false)
const foundersBlurb = ref('')
const pageDescription = ref('')

const setLoadingMembers = (value) => {
    loadingMembers.value = value
}

const { translatePage, handleLanguageChange: handleTranslation } = useDataTranslation(componentRef, loadingMembers, '.min-h-screen.bg-white', setLoadingMembers)

const handleLanguageChange = async () => {
    await loadTeamMembers()
    await loadPageContent()
    await handleTranslation()
}

onMounted(() => {
    window.addEventListener('language-changed', handleLanguageChange)
})

onUnmounted(() => {
    window.removeEventListener('language-changed', handleLanguageChange)
})

const founders = computed(() => {
    return teamMembers.value.filter(member => member.member_type === 'founder')
})

const team = computed(() => {
    return teamMembers.value.filter(member => member.member_type !== 'founder')
})

const openDialog = async (member) => {
    selectedMember.value = member
    document.body.style.overflow = 'hidden'

    await nextTick()
    if (modalRef.value && translationService.getLanguage() !== 'en') {
        await translationService.translateElement(modalRef.value)
    }
}

const closeDialog = () => {
    selectedMember.value = null
    document.body.style.overflow = ''
}

const loadPageContent = async () => {
    try {
        const page = await publicService.getPage('Our Team')
        if (page?.description) {
            pageDescription.value = page.description
        }
        if (page?.sections && page.sections.length > 0) {
            const foundersSection = page.sections.find(section =>
                section.title?.toLowerCase().includes('founder')
            )
            if (foundersSection?.content) {
                foundersBlurb.value = foundersSection.content.replace(/<[^>]*>/g, '').trim()
            }
        }
    } catch (error) {
        if (error.response?.status !== 404) {
            console.error('Failed to load page content:', error)
        }
    }
}

const loadTeamMembers = async () => {
    loadingMembers.value = true
    try {
        const members = await publicService.getTeamMembers()
        teamMembers.value = Array.isArray(members) ? members.map(member => ({
            name: member.name,
            image: member.image_url || '',
            bio: member.bio || '',
            member_type: member.member_type || 'team'
        })) : []
    } catch (error) {
        console.error('Failed to load team members:', error)
        teamMembers.value = []
    } finally {
        loadingMembers.value = false
    }
}

onMounted(async () => {
    await loadPageContent()
    await loadTeamMembers()
    await translatePage()
})
</script>

<style scoped></style>
